import {
  registerDecorator,
  ValidationArguments,
  ValidationOptions,
} from 'class-validator';

/**
 * True when `value` is a finite number with at most `places` decimal places.
 * class-validator's IsNumber({ maxDecimalPlaces }) reads the digits after the
 * dot of value.toString(), which throws for exponent notation (1e-7 has no
 * dot), so a request with such a value would answer 500 instead of 400.
 */
export const hasMaxDecimalPlaces = (
  value: unknown,
  places: number,
): boolean => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return false;
  }

  const scaled = value * 10 ** places;
  return Math.abs(scaled - Math.round(scaled)) < 1e-9;
};

/** Validation decorator: at most `places` decimal places, exponent-safe. */
export function MaxDecimalPlaces(
  places: number,
  validationOptions?: ValidationOptions,
) {
  return (object: object, propertyName: string): void => {
    registerDecorator({
      name: 'maxDecimalPlaces',
      target: object.constructor,
      propertyName,
      constraints: [places],
      options: validationOptions,
      validator: {
        validate: (value: unknown, args: ValidationArguments) =>
          hasMaxDecimalPlaces(value, args.constraints[0]),
        defaultMessage: (args: ValidationArguments) =>
          `${args.property} must have at most ${args.constraints[0]} decimal places`,
      },
    });
  };
}
